import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";

import { api, ApiError, invalidateCsrfToken } from "./api";

export type Role = "admin" | "user";

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  is_admin: boolean;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  /** ISO timestamp; sessions end at the coming Sunday midnight. Present on /auth/me/ and login. */
  session_expires_at?: string;
}

export const meQueryKey = ["auth", "me"] as const;

/** The current user, or null when the session is anonymous. */
export const meQueryOptions = queryOptions<User | null>({
  queryKey: meQueryKey,
  queryFn: async () => {
    try {
      return await api<User>("/auth/me/");
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) return null;
      throw error;
    }
  },
  staleTime: 5 * 60 * 1000,
});

export interface LoginInput {
  email: string;
  password: string;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api<User>("/auth/login/", { method: "POST", body: input }),
    onSuccess: (user) => {
      invalidateCsrfToken(); // Django rotates the token on login
      queryClient.setQueryData(meQueryKey, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const router = useRouter();
  return useMutation({
    mutationFn: () => api<void>("/auth/logout/", { method: "POST" }),
    onSettled: async () => {
      invalidateCsrfToken();
      queryClient.setQueryData(meQueryKey, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
      await router.navigate({ to: "/login" });
    },
  });
}

export function displayName(user: Pick<User, "first_name" | "last_name" | "email">): string {
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name || user.email;
}
